import React, { useState } from 'react';
import { useNavigate, Link, useOutletContext } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useData } from '../contexts/DataContext';
import { supabase } from '../supabaseClient';
import { backendUrl } from '../config';
import Spinner from './Spinner';
import { isTopicSafe, isTextSafe } from '../utils/safeForAds';
import { getUserTimezone } from '../utils/timezoneUtils';
import { SUPPORTED_LANGUAGES, validateLanguageSelection, normalizeLanguageName } from '../utils/languageValidation';
import { updateCountsAfterWordChange } from '../utils/countUpdates';

const GettingStartedPage = () => {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const { refreshModules } = useData();
  const { refetchUsage } = useOutletContext() || {};


  const [fromLang, setFromLang] = useState('en');
  const [toLang, setToLang] = useState('es');
  const [topic, setTopic] = useState('');
  const [dialect, setDialect] = useState('');
  const [moduleName, setModuleName] = useState('');
  const [wordCount, setWordCount] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');



  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate inputs
    if (!fromLang || !toLang || !topic.trim() || !moduleName.trim()) {
      setError(t('pleaseFillAllRequiredFields'));
      setLoading(false);
      return;
    }

    if (moduleName.trim().length > 150) {
      setError(t('moduleNameMaxLength') || 'Module name cannot exceed 150 characters');
      setLoading(false);
      return;
    }

    // Check for profanity in module name
    if (!isTextSafe(moduleName.trim())) {
      setError(t('inappropriateContentDetected'));
      setLoading(false);
      return;
    }

    // Validate language selection
    const validation = validateLanguageSelection(fromLang, toLang);
    if (!validation.valid) {
      setError(t(validation.error) || validation.error);
      setLoading(false);
      return;
    }

    // Check topic and dialect safety
    if (!isTopicSafe(topic)) {
      setError(t('inappropriateContentDetected'));
      setLoading(false);
      return;
    }
    if (!isTopicSafe(dialect)) {
      setError(t('inappropriateContentDetected'));
      setLoading(false);
      return;
    }

    try {
      // Get user session
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) {
        setError(t('pleaseLogIn'));
        setLoading(false);
        return;
      }

      // Get the current max position of modules for this user
      const { data: existingModules, error: countError } = await supabase
        .from('modules_new')
        .select('position')
        .eq('user_id', userId)
        .order('position', { ascending: false })
        .limit(1);
      if (countError) {
        console.error('Error fetching module count:', countError);
        setError(t('couldNotCreateModule') || 'Could not create module.');
        setLoading(false);
        return;
      }
      const position = (!existingModules || existingModules.length === 0)
        ? 0
        : (existingModules[0].position + 1);

      // 1. Insert new module into modules_new
      const { data: insertedModule, error: insertError } = await supabase
        .from('modules_new')
        .insert({
          user_id: userId,
          from_lang: normalizeLanguageName(fromLang),
          to_lang: normalizeLanguageName(toLang),
          dialect: dialect.trim().substring(0, 50),
          name: moduleName.trim().substring(0, 150),
          position,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();
      if (insertError || !insertedModule) {
        setError(t('couldNotCreateModule') || 'Could not create module.');
        setLoading(false);
        return;
      }

      // 2. Insert new word group for the topic (do NOT include dialect)
      // Get the maximum position for word groups in this module to safely assign next position
      const { data: existingGroups, error: groupsError } = await supabase
        .from('word_groups')
        .select('position')
        .eq('module_id', insertedModule.id)
        .order('position', { ascending: false })
        .limit(1);

      if (groupsError) {
        console.error('Error fetching existing word groups:', groupsError);
        setError(t('couldNotCreateWordGroup') || 'Could not create word group.');
        setLoading(false);
        return;
      }

      const groupPosition = (!existingGroups || existingGroups.length === 0)
        ? 0
        : (existingGroups[0].position + 1);
      const groupInsertPayload = {
        user_id: userId,
        module_id: insertedModule.id,
        name: topic.trim().substring(0, 100),
        position: groupPosition,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      const { data: insertedGroup, error: groupInsertError } = await supabase
        .from('word_groups')
        .insert(groupInsertPayload)
        .select()
        .single();
      if (groupInsertError || !insertedGroup) {
        console.error('Word group insert error:', groupInsertError, insertedGroup, groupInsertPayload);
        setError((groupInsertError && groupInsertError.message) ? `Could not create word group: ${groupInsertError.message}` : (t('couldNotCreateWordGroup') || 'Could not create word group.'));
        setLoading(false);
        return;
      }

      // 3. Generate words with AI
      const { data: { session: freshSession } } = await supabase.auth.getSession();
      const accessToken = freshSession?.access_token;
      const aiRes = await fetch(`${backendUrl}/gemini-generate-words`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: topic.trim().substring(0, 100),
          fromLang: normalizeLanguageName(fromLang),
          toLang: normalizeLanguageName(toLang),
          wordCount,
          dialect: dialect.trim().substring(0, 50),
          accessToken,
          timezone: getUserTimezone(),
        }),
      });
      const aiData = await aiRes.json();
      if (!aiData.words || !Array.isArray(aiData.words) || aiData.words.length === 0) {
        setError(t('couldNotGenerateWords') || 'Could not generate words.');
        setLoading(false);
        return;
      }
      // Normalize AI words to { left, right }
      const fromKey = normalizeLanguageName(fromLang);
      const toKey = normalizeLanguageName(toLang);
      const normalizedPairs = aiData.words.map(pair => ({
        left: pair[fromKey],
        right: pair[toKey],
      }));
      const validPairs = normalizedPairs.filter(
        pair => pair && typeof pair.left === 'string' && pair.left.trim() && typeof pair.right === 'string' && pair.right.trim()
      );
      if (validPairs.length === 0) {
        setError(t('couldNotGenerateWords') || 'Could not generate words.');
        setLoading(false);
        return;
      }

      // 4. Insert words into words table (SRS data now stored in word_srs_data table)
      const wordsToInsert = validPairs.map((pair, idx) => ({
        group_id: insertedGroup.id,
        from_text: pair.left.trim(),
        to_text: pair.right.trim(),
        position: idx,
      }));
      await supabase.from('words').insert(wordsToInsert);

      // Update counts after word insertion
      await updateCountsAfterWordChange(insertedGroup.id);

      // Refresh dashboard modules to show the new module
      refreshModules();
      if (refetchUsage) refetchUsage();

      // 5. Route to the new word group page
      navigate(`/module/${insertedModule.id}/group/${insertedGroup.id}`);
      setLoading(false);
    } catch (error) {
      console.error('Error in getting started:', error);
      setError(t('couldNotConnectToAI'));
      setLoading(false);
    }
  };

  return (
    <div className="getting-started-container" style={{
      maxWidth: 600,
      margin: '60px auto',
      padding: 32,
      background: '#fff',
      borderRadius: 14,
      boxShadow: '0 4px 24px rgba(0,0,0,0.09)',
      direction: isRTL ? 'rtl' : 'ltr',
      textAlign: isRTL ? 'right' : 'left'
    }}>
      <h1 style={{ color: '#386641', marginBottom: 24, textAlign: 'center' }}>
        {t('gettingStarted') || 'Getting Started'}
      </h1>

      <p style={{ fontSize: '1.1rem', marginBottom: 32, textAlign: 'center', color: '#666' }}>
        {t('gettingStartedDescription') || 'Let\'s create your first language learning module with AI-generated vocabulary!'}
      </p>

      {loading && <Spinner message={t('generatingWords')} size="large" overlay />}

      <form onSubmit={handleSubmit}>
        <div className="form-group" style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
            {t('iSpeak')} *
          </label>
          <select
            value={fromLang}
            onChange={(e) => setFromLang(e.target.value)}
            required
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: 8,
              border: '1px solid #ddd',
              fontSize: '1rem'
            }}
          >
            {SUPPORTED_LANGUAGES.map(lang => (
              <option key={lang.code} value={lang.code}>
                {lang.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group" style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
            {t('iWantToLearn')} *
          </label>
          <select
            value={toLang}
            onChange={(e) => setToLang(e.target.value)}
            required
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: 8,
              border: '1px solid #ddd',
              fontSize: '1rem'
            }}
          >
            {SUPPORTED_LANGUAGES.map(lang => (
              <option key={lang.code} value={lang.code}>
                {lang.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group" style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
            {t('regionDialectOptional') || 'Region/Dialect (optional)'}
          </label>
          <input
            type="text"
            value={dialect}
            onChange={(e) => setDialect(e.target.value.substring(0, 50))}
            placeholder={t('dialectPlaceholder') || 'e.g., Mexican, Colombian, Castilian'}
            maxLength={50}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: 8,
              border: '1px solid #ddd',
              fontSize: '1rem'
            }}
          />
          {dialect.length >= 50 && (
            <div style={{
              color: '#d32f2f',
              fontSize: '0.8rem',
              marginTop: '4px'
            }}>
              {t('dialectMaxLength') || 'Dialect cannot exceed 50 characters'}
            </div>
          )}
        </div>

        <div className="form-group" style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>
            {t('moduleName') || 'Module Name'} *
          </label>
          <p style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#667085' }}>
            {t('moduleNameHelp') || 'A module is a collection of related word groups and flashcards. Give it a clear name so you can find it later.'}
          </p>
          <input
            type="text"
            value={moduleName}
            onChange={(e) => setModuleName(e.target.value.substring(0, 150))}
            placeholder={t('moduleNamePlaceholder') || 'e.g., Spanish Basics, French Conversation'}
            maxLength={150}
            required
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: 8,
              border: '1px solid #ddd',
              fontSize: '1rem'
            }}
          />
          {moduleName.length >= 150 && (
            <div style={{
              color: '#d32f2f',
              fontSize: '0.8rem',
              marginTop: '4px'
            }}>
              {t('moduleNameMaxLength') || 'Module name cannot exceed 150 characters'}
            </div>
          )}
        </div>

        <div className="form-group" style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>
            {t('topic') || 'Topic'} *
          </label>
          <p style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#667085' }}>
            {t('topicHelp') || 'The topic tells the AI what kind of vocabulary to generate for your first word group (for example, food, travel, or business).'}
          </p>
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value.substring(0, 100))}
            placeholder={t('topicPlaceholder') || 'e.g., food, travel, business'}
            maxLength={100}
            required
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: 8,
              border: '1px solid #ddd',
              fontSize: '1rem'
            }}
          />
          {topic.length >= 100 && (
            <div style={{
              color: '#d32f2f',
              fontSize: '0.8rem',
              marginTop: '4px'
            }}>
              {t('topicMaxLength') || 'Topic cannot exceed 100 characters'}
            </div>
          )}
        </div>

        <div className="form-group" style={{ marginBottom: 32 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
            {t('numberOfWords') || 'Number of words'} *
          </label>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.75rem',
            marginBottom: '1.5rem',
            justifyContent: 'center'
          }}>
            {[5, 10, 25, 50, 100].map(count => (
              <button
                key={count}
                type="button"
                onClick={() => setWordCount(count)}
                style={{
                  backgroundColor: wordCount === count ? '#386641' : '#f8f9fa',
                  border: `1px solid ${wordCount === count ? '#386641' : '#dee2e6'}`,
                  padding: '0.5rem 1rem',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: wordCount === count ? 700 : 500,
                  color: wordCount === count ? '#fff' : '#000',
                  transition: 'all 0.2s ease',
                  minWidth: '60px'
                }}
                onMouseEnter={(e) => {
                  if (wordCount !== count) {
                    e.target.style.backgroundColor = '#e9ecef';
                    e.target.style.borderColor = '#adb5bd';
                  }
                }}
                onMouseLeave={(e) => {
                  if (wordCount !== count) {
                    e.target.style.backgroundColor = '#f8f9fa';
                    e.target.style.borderColor = '#dee2e6';
                  }
                }}
              >
                {count}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div style={{
            color: '#d32f2f',
            marginBottom: 20,
            padding: '12px',
            backgroundColor: '#ffebee',
            borderRadius: 8,
            border: '1px solid #ffcdd2',
            textAlign: 'center'
          }}>
            <div>{error}</div>
            {error.includes('Want to learn more?') && (
              <div style={{ marginTop: '0.8rem', textAlign: 'center' }}>
                <Link
                  to="/store"
                  className="btn btn-primary"
                  style={{
                    display: 'inline-block',
                    textDecoration: 'none',
                    background: 'linear-gradient(90deg, #a8e063 0%, #56ab2f 100%)',
                    color: '#fff',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: 'none',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                >
                  {t('upgradeToPremium')}
                </Link>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary"
            style={{
              fontSize: '1.1rem',
              padding: '12px 32px',
              borderRadius: 8,
              fontWeight: 600,
              background: 'linear-gradient(90deg, #a8e063 0%, #56ab2f 100%)',
              border: 'none',
              color: '#fff',
              boxShadow: '0 2px 8px rgba(56,102,65,0.10)'
            }}
          >
            {loading ? t('creating') || 'Creating...' : t('createWords') || 'Create Words'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GettingStartedPage; 